"use client";
import React, { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

export interface BrandingData {
  brandName?: string;
  appSubtitle?: string;
  primaryColor?: string;
  logoText?: string;
  loginBanner?: string;
  footerText?: string;
  logoUrl?: string | null;
  loginBgUrl?: string | null;
  faviconUrl?: string | null;
}

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:4000";

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.replace("#", "").trim();
  if (m.length === 3) {
    const r = parseInt((m[0] ?? "") + (m[0] ?? ""), 16);
    const g = parseInt((m[1] ?? "") + (m[1] ?? ""), 16);
    const b = parseInt((m[2] ?? "") + (m[2] ?? ""), 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) return null;
    return { r, g, b };
  }
  if (m.length === 6) {
    const r = parseInt(m.slice(0, 2), 16);
    const g = parseInt(m.slice(2, 4), 16);
    const b = parseInt(m.slice(4, 6), 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) return null;
    return { r, g, b };
  }
  return null;
}

function applyBranding(b: BrandingData | null) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const color = b?.primaryColor ?? "#2563EB";
  root.style.setProperty("--brand-color", color);
  const rgb = hexToRgb(color);
  if (rgb) root.style.setProperty("--brand-rgb", `${rgb.r} ${rgb.g} ${rgb.b}`);
  if (b?.brandName) {
    document.title = b.brandName;
    const meta = document.querySelector('meta[name="application-name"]');
    if (meta) meta.setAttribute("content", b.brandName);
  }
  if (b?.faviconUrl) {
    let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = b.faviconUrl;
  }
}

let cached: BrandingData | null = null;
let inflight: Promise<BrandingData | null> | null = null;

export async function fetchBranding(): Promise<BrandingData | null> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/branding`, { cache: "no-store" });
      if (!res.ok) return null;
      cached = await res.json();
      return cached;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  // 用 inline script 在 hydration 之前先写一次 title, 避免闪 "TaskFlow"
  // 同时用 useEffect 在 hydration 后再次写, 处理路由变化
  React.useEffect(() => {
    let cancelled = false;
    const apply = () => fetchBranding().then((b) => { if (b && !cancelled) applyBranding(b); });
    apply();
    // 路由变化或 metadata 重新写入时, 100ms 内持续尝试覆盖
    const intervals: number[] = [];
    for (let i = 0; i < 5; i++) intervals.push(window.setTimeout(apply, 50 * (i + 1)));
    return () => { cancelled = true; intervals.forEach((id) => clearTimeout(id)); };
  }, [pathname]);

  return <>{children}</>;
}

export function useBrandingSync() {
  const pathname = usePathname();
  useEffect(() => {
    fetchBranding().then((b) => {
      if (b) applyBranding(b);
    });
  }, [pathname]);
}

export { applyBranding };
